import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import axios from 'axios'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import { connect } from 'react-redux'
import Loader from '~components/Loader'
import { loadContent } from '~redux/content'
import { loadRecentProducts } from '~redux/shop'
import BrandLink from '~components/BrandLink'
import Hero from '~components/Hero'
import Card from '~components/Card'
import Carousel from '~components/Carousel'
import { getOrderStatus } from '~helpers/main'
import { pageView } from '~helpers/ga'
import Prompt from '~components/ui/Prompt'
import Cookie from 'js-cookie'

const SUSPENSION_COOKIE = 'acknowledgeSuspension'

const Dashboard = ({ content, recentProducts, loadContent, loadRecentProducts, isSuspended }) => {
  const [state, setState] = useState({
    isSuspended: false,
    showSuspensionPrompt: false,
    orders: false
  })

  useEffect(() => {
    pageView()
    loadContent('dashboard')
    loadRecentProducts()
    setSuspensionStatus()

    axios.get('/api/account/orders/recent')
      .then(response => {
        setState(prevState => ({
          ...prevState,
          orders: response.data
        }))
      })
  }, [loadContent, loadRecentProducts])

  const setSuspensionStatus = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      isSuspended,
      showSuspensionPrompt: showSuspensionPrompt()
    }))

    if (!isSuspended) {
      Cookie.remove('acknowledgeSuspension')
    }
  }, [isSuspended])

  const showSuspensionPrompt = useCallback(() => {
    const acknowledgeSuspension = parseInt(Cookie.get(SUSPENSION_COOKIE))
    return isSuspended && !acknowledgeSuspension
  }, [isSuspended])

  const closeSuspensionPrompt = useCallback(() => {
    Cookie.set(SUSPENSION_COOKIE, 1, { expires: 1 })
    setState(prevState => ({ ...prevState, showSuspensionPrompt: false }))
  }, [])

  const hasRecentlyViewedProducts = useCallback(() => {
    return recentProducts && recentProducts.length
  }, [recentProducts])

  if (!content) {
    return <Loader />
  }

  return (
    <>
      <Hero
        title={content.fields.hero_text}
        image={content.fields.hero_image}
        showCta={!state.isSuspended}
        className="-mt-8"
      />

      <div className="px-4 mx-auto my-10 xxl:container md:px-8 xxl:px-0 xl:my-20">
        <div className="flex flex-wrap items-stretch -mx-2 -my-5 lg:-mx-5">
          {state.orders &&
          <section className="w-full px-2 my-5 lg:w-1/2 lg:px-5">
            <h2 className="mb-5 md:mb-10">
              Order status
            </h2>

            <div className="w-full overflow-x-auto">
              <table className="w-full border border-pink">
                <tbody>
                  {state.orders.map((order, index) => {
                    const classes = []

                    if (index % 2 === 0) {
                      classes.push('bg-pink')
                    }

                    return (
                      <tr key={order.id}>
                        <td className={classNames('p-4 sm:p-6', classes)}>
                          <Link to={`/account/orders/${order.id}`}>
                            {order.id}
                          </Link>
                        </td>
                        <td className={classNames('p-4 sm:p-6 text-center', classes)}>
                          {moment(order.date).format('DD-MM-YYYY')}
                        </td>
                        <td className={classNames('p-4 sm:p-6 text-right', classes)}>
                          {getOrderStatus(order.status)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </section>}

          <section className="w-full px-2 my-5 lg:w-1/2 lg:px-5 lg:flex lg:flex-col">
            <h2 className="mb-5 md:mb-10">
              Media hub
            </h2>

            <Carousel
              items={content.fields.ctas}
              className="flex flex-grow w-full min-h-80 lg:min-h-66 lg:max-h-80"
            />
          </section>
        </div>
      </div>

      <section className="px-4 mx-auto my-10 xxl:container md:px-8 xxl:px-0 xl:my-20">
        <h2 className="mb-5 md:mb-10">
          Quick links
        </h2>

        <div className="flex flex-wrap -mx-1 -my-1 md:-mx-2 md:-my-3 xxl:-mx-5 xl:-my-10">
          {content.fields.quick_links.map((link, index) => (
            <div className="w-1/2 px-1 my-1 md:w-1/3 lg:w-1/5 md:px-2 xxl:px-5 md:my-3 xl:my-10" key={index}>
              <BrandLink
                title={link.attributes.text}
                image={{
                  src: link.attributes.image.lg,
                  alt: link.attributes.text
                }}
                url={link.attributes.link}
              />
            </div>
          ))}
        </div>
      </section>

      {(hasRecentlyViewedProducts() && !state.isSuspended) && (
        <section className="px-4 mx-auto mt-10 xxl:container md:px-8 xxl:px-0 mb-15 xl:my-20">
          <h2 className="mb-5 md:mb-10">
            Recently viewed products
          </h2>

          <div className="flex flex-wrap -mx-1 -my-5 md:-mx-4 lg:-mx-2 xl:-mx-5 xl:-my-10">
            {recentProducts.map(product => {
              const mainImage = product.images.find(i => i.is_thumbnail)

              return (
                <div key={product.id} className="w-1/2 px-1 my-5 md:w-1/2 lg:w-1/4 md:px-4 lg:px-2 xl:px-5 xl:my-10">
                  <Card
                    image={{
                      src: mainImage ? mainImage.url_standard : null,
                      alt: product.name
                    }}
                    subtitle={`${product.custom_fields.brand} | ${product.custom_fields.season}`}
                    title={product.name}
                    url={product.style_url}
                    colour={product.custom_fields.colour}
                    styleCode={product.custom_fields.style_code}
                  />
                </div>
              )
            })}
          </div>
        </section>
      )}

      {showSuspensionPrompt() && (
        <Prompt
          title="Account Suspended"
          text="<p>Oops! You currently have invoices that are overdue for payment, which will temporarily prevent orders being processed. <a href='/account/payment'>Click here</a> to make a payment. Once approved, you will be able to resume full access to the B2B site.</p>"
          onClose={() => closeSuspensionPrompt()}
          onConfirm={() => closeSuspensionPrompt()}
        />
      )}
    </>
  )
}

Dashboard.propTypes = {
  content: PropTypes.object,
  recentProducts: PropTypes.array,
  loadContent: PropTypes.func,
  loadRecentProducts: PropTypes.func,
  isSuspended: PropTypes.bool,
  countryCode: PropTypes.string
}

const mapStateToProps = (state) => ({
  content: state.content.dashboard || null,
  recentProducts: state.shop.recentProducts,
  isSuspended: state.auth.isSuspended,
  countryCode: state.auth.account?.stockist?.country
})

const mapDispatchToProps = (dispatch) => ({
  loadContent: (page) => dispatch(loadContent(page)),
  loadRecentProducts: () => dispatch(loadRecentProducts())
})

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard)
